import React, { useRef, useState, useMemo, useEffect } from "react";
import PreprocessAndRenderLatex from "../../../../../../components/ProcessRenderLatex";
import { addIdsToParagraphs } from "../../../../../../utils/utlis";
import "./StudentAssignmentPreview.css";
import { toast } from "react-toastify";
import {
    YT_PLAYER_SELECTOR,
    getYoutubeVideoId,
    playYoutubeAt,
    postYoutubeCommand,
    withYoutubeJsApi,
    ytSecondsFromUrl,
} from "../../teacher/lessons/youtubePlayer";

function StudentLessonPreview({ lesson, isVideoBlocked }) {
    const scrollableDivRef = useRef(null);
    const lessonBodyRef = useRef(null);
    const [lessondtls, setLesson] = useState(lesson || {});
    const [lessonJson, setLessonJson] = useState([]);

    // ── extract EVERY YouTube iframe OUT of the lesson HTML (0, 1, 2, 3 … videos) ──
    const { modifiedDescription, youtubeVideos } = useMemo(() => {
        const raw = lessondtls?.description || "";
        if (!raw) return { modifiedDescription: "", youtubeVideos: [] };

        const videos = [];
        let cleanHtml = raw;
        try {
            const doc = new DOMParser().parseFromString(raw, "text/html");
            doc.querySelectorAll('iframe[src*="youtube.com/embed/"], iframe[src*="youtube-nocookie.com/embed/"]')
                .forEach((iframe) => {
                    const src = iframe.getAttribute("src") || "";
                    if (src) {
                        videos.push({
                            src: withYoutubeJsApi(src), // enablejsapi=1 so postMessage seek/pause works
                            videoId: getYoutubeVideoId(src),
                            title: iframe.getAttribute("title") || "YouTube video player",
                        });
                    }
                    iframe.remove(); // remove so it doesn't render inline / twice
                });
            cleanHtml = doc.body.innerHTML;
        } catch (e) {
            console.error("Failed to extract lesson videos:", e);
            cleanHtml = raw;
        }

        return {
            modifiedDescription: addIdsToParagraphs(cleanHtml, lessonJson),
            youtubeVideos: videos,
        };
    }, [lessondtls?.description, lessonJson]);

    useEffect(() => {
        setLesson(lesson || {});
    }, [lesson]);

    // ── timestamp click → play the video matching the button's data-url ──
    useEffect(() => {
        const container = lessonBodyRef.current;
        if (!container) return;
        if (isVideoBlocked) return;

        const onClick = (e) => {
            const btn = e.target.closest(".youtube_timestamp");
            if (!btn) return;
            e.preventDefault();
            e.stopPropagation();

            const url = btn.getAttribute("data-url") || "";
            const seconds = ytSecondsFromUrl(url, btn.textContent);
            // plays the matching player, pauses every other one
            const iframe = playYoutubeAt(container, url, seconds);

            // video isn't embedded in this lesson → open the link instead
            if (!iframe) {
                if (url) window.open(url, "_blank", "noopener,noreferrer");
                return;
            }
            iframe.scrollIntoView({ behavior: "smooth", block: "center" });
        };

        container.addEventListener("click", onClick);
        return () => container.removeEventListener("click", onClick);
    }, [modifiedDescription, lessonJson, isVideoBlocked]);

    // recording started → pause every video in this preview
    useEffect(() => {
        if (!isVideoBlocked) return;
        lessonBodyRef.current
            ?.querySelectorAll(YT_PLAYER_SELECTOR)
            .forEach((iframe) => {
                try {
                    postYoutubeCommand(iframe, "pauseVideo");
                } catch (e) { /* iframe not ready */ }
            });
    }, [isVideoBlocked]);

    return (
        <>
            <div className="student-lesson-preview-section">
                <div className='student-lesson-preview-wrapper'>
                    <div className='student-lesson-preview-container'>
                        <div className='student-lesson-preview-folder'>
                            <div className='student-lesson-preview-list' ref={lessonBodyRef}>
                                {youtubeVideos.map((video, i) => (
                                    <div
                                        key={`yt-${i}`}
                                        style={{
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            margin: "20px 0",
                                        }}
                                    >
                                        {/* sized box = anchor so the block overlay covers exactly this video */}
                                        <div style={{ position: "relative", width: "500px", maxWidth: "100%", height: "280px" }}>
                                            <iframe
                                                data-yt-player="true"
                                                data-yt-id={video.videoId}
                                                width="100%"
                                                height="100%"
                                                style={{ display: "block" }}
                                                src={video.src}
                                                title={video.title}
                                                frameBorder="0"
                                                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                                                referrerPolicy="strict-origin-when-cross-origin"
                                                allowFullScreen
                                            />

                                            {isVideoBlocked && (
                                                <div
                                                    style={{
                                                        position: "absolute",
                                                        inset: 0,
                                                        zIndex: 2,
                                                        cursor: "not-allowed",
                                                        background: "rgba(0,0,0,0.35)", // or "transparent" for invisible block
                                                    }}
                                                    onClick={(e) => {
                                                        e.preventDefault();
                                                        e.stopPropagation();
                                                        toast.info("Video can't play while recording.");
                                                    }}
                                                />
                                            )}
                                        </div>
                                    </div>
                                ))}
                                {lessondtls?.is_skip ? (
                                    // teacher skipped the lesson and went straight to the assignment
                                    <div
                                        style={{
                                            textAlign: "center",
                                            padding: "40px 16px",
                                            color: "#555",
                                            fontSize: "16px",
                                            fontWeight: 500,
                                        }}
                                    >
                                        This lesson was skipped by the teacher.
                                    </div>
                                ) : (
                                    <PreprocessAndRenderLatex
                                        ref={scrollableDivRef}
                                        content={modifiedDescription}
                                    />
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    )
}

export default StudentLessonPreview;